/**
 * ReimbursementsAdmin.jsx — FM4C Staff Reimbursement Workspace
 *
 * 6-tab staff workspace for the FM4C Reimbursement Core.
 * Authorization: FM4C_IMPLEMENTATION_PLAN.md §15.1
 * Design authority: FM4_D1_DESIGN.md (FROZEN 2026-09-13)
 *
 * Tabs:
 *  1. Schemes      — configure reimbursement scheme definitions
 *  2. Records      — all student records with 4-axis status
 *  3. Eligibility Queue — ELIGIBILITY_PENDING / CONDITIONALLY_ELIGIBLE
 *  4. Claims Queue — READY_TO_CLAIM / CLAIM_SUBMITTED
 *  5. Sanctions    — AWAITING_SANCTION / REVERSAL_PENDING
 *  6. Receipts     — PARTIALLY_RECEIVED / FULLY_RECEIVED
 */

import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, Users, CheckCircle, FileText, Banknote, ReceiptText,
  Plus, RefreshCw, ChevronDown, ChevronRight, AlertTriangle,
  Clock, Check, X, BookOpen, Landmark, Shield
} from "lucide-react";
import { Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) => {
  const n = parseFloat(v || 0);
  return isNaN(n) ? "₹0.00" : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

const AXIS_COLORS = {
  ELIGIBILITY_PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  ELIGIBLE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  INELIGIBLE: "bg-red-50 text-red-700 border-red-200",
  CONDITIONALLY_ELIGIBLE: "bg-orange-50 text-orange-700 border-orange-200",
  NOT_STARTED: "bg-slate-50 text-slate-500 border-slate-200",
  DOCUMENTS_PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  READY_TO_CLAIM: "bg-blue-50 text-blue-700 border-blue-200",
  CLAIM_SUBMITTED: "bg-indigo-50 text-indigo-700 border-indigo-200",
  ACKNOWLEDGED: "bg-indigo-50 text-indigo-600 border-indigo-200",
  CLAIM_WITHDRAWN: "bg-slate-50 text-slate-500 border-slate-200",
  AWAITING_SANCTION: "bg-amber-50 text-amber-700 border-amber-200",
  SANCTIONED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARTIALLY_SANCTIONED: "bg-teal-50 text-teal-700 border-teal-200",
  REJECTED: "bg-red-50 text-red-700 border-red-200",
  SANCTION_REVOKED: "bg-orange-50 text-orange-700 border-orange-200",
  REVERSAL_PENDING: "bg-orange-50 text-orange-600 border-orange-200",
  NONE_RECEIVED: "bg-slate-50 text-slate-500 border-slate-200",
  PARTIALLY_RECEIVED: "bg-blue-50 text-blue-700 border-blue-200",
  FULLY_RECEIVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  RECONCILED: "bg-teal-50 text-teal-700 border-teal-200",
  CLOSED: "bg-slate-100 text-slate-500 border-slate-200",
};

function AxisBadge({ value }) {
  const cls = AXIS_COLORS[value] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {value?.replace(/_/g, " ")}
    </span>
  );
}

function FourAxisRow({ record }) {
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      <AxisBadge value={record.eligibility_status} />
      <span className="text-slate-300 text-xs">·</span>
      <AxisBadge value={record.claim_status} />
      <span className="text-slate-300 text-xs">·</span>
      <AxisBadge value={record.sanction_status} />
      <span className="text-slate-300 text-xs">·</span>
      <AxisBadge value={record.receipt_status} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none";

// ── TAB 1: Schemes ────────────────────────────────────────────────────────────

function SchemesTab({ token }) {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    scheme_code: "", scheme_name: "", sponsoring_authority: "",
    coverage_type: "FULL_FEE", sanction_reversal_grace_days: 30,
    partial_sanction_handling: "ACCEPT_PARTIAL",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/api/finance/fm4c/schemes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { const d = await res.json(); setSchemes(d.schemes || []); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API}/api/finance/fm4c/schemes`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      toast.success("Scheme created");
      setShowCreate(false);
      setForm({ scheme_code: "", scheme_name: "", sponsoring_authority: "", coverage_type: "FULL_FEE", sanction_reversal_grace_days: 30, partial_sanction_handling: "ACCEPT_PARTIAL" });
      load();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Failed to create scheme");
    }
  };

  const retire = async (id) => {
    if (!window.confirm("Retire this scheme? New records cannot be created under it.")) return;
    const res = await fetch(`${API}/api/finance/fm4c/schemes/${id}/retire`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { toast.success("Scheme retired"); load(); }
    else toast.error("Failed to retire scheme");
  };

  return (
    <div className="space-y-4" data-testid="schemes-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{schemes.length} configured scheme{schemes.length !== 1 ? "s" : ""}</p>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
          data-testid="create-scheme-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          New Scheme
        </button>
      </div>

      {showCreate && (
        <Section title="Create Reimbursement Scheme">
          <form onSubmit={create} className="grid grid-cols-2 gap-4" data-testid="create-scheme-form">
            <Field label="Scheme Code *">
              <input className={inputCls} required value={form.scheme_code} onChange={e => setForm(f => ({ ...f, scheme_code: e.target.value }))} data-testid="scheme-code-input" />
            </Field>
            <Field label="Scheme Name *">
              <input className={inputCls} required value={form.scheme_name} onChange={e => setForm(f => ({ ...f, scheme_name: e.target.value }))} data-testid="scheme-name-input" />
            </Field>
            <Field label="Sponsoring Authority *">
              <input className={inputCls} required value={form.sponsoring_authority} onChange={e => setForm(f => ({ ...f, sponsoring_authority: e.target.value }))} data-testid="sponsoring-authority-input" />
            </Field>
            <Field label="Coverage Type">
              <select className={inputCls} value={form.coverage_type} onChange={e => setForm(f => ({ ...f, coverage_type: e.target.value }))}>
                <option value="FULL_FEE">Full Fee</option>
                <option value="TUITION_ONLY">Tuition Only</option>
                <option value="HOSTEL_ONLY">Hostel Only</option>
                <option value="PARTIAL_PERCENTAGE">Partial Percentage</option>
                <option value="PARTIAL_AMOUNT">Partial Amount</option>
              </select>
            </Field>
            <Field label="Partial Sanction Handling">
              <select className={inputCls} value={form.partial_sanction_handling} onChange={e => setForm(f => ({ ...f, partial_sanction_handling: e.target.value }))}>
                <option value="ACCEPT_PARTIAL">Accept Partial</option>
                <option value="HOLD_FOR_FULL">Hold for Full</option>
              </select>
            </Field>
            <Field label="Reversal Grace Days">
              <input type="number" className={inputCls} min={0} value={form.sanction_reversal_grace_days} onChange={e => setForm(f => ({ ...f, sanction_reversal_grace_days: parseInt(e.target.value) }))} />
            </Field>
            <div className="col-span-2 flex gap-2 pt-2">
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors" data-testid="submit-scheme-btn">Create Scheme</button>
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">Cancel</button>
            </div>
          </form>
        </Section>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : schemes.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No schemes configured yet. (INV-FM4-15: No default schemes in production.)</p>
        </div>
      ) : (
        <div className="space-y-2">
          {schemes.map(s => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-start justify-between" data-testid={`scheme-row-${s.scheme_code}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-slate-800">{s.scheme_name}</span>
                  <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-500 font-mono">{s.scheme_code}</span>
                  <span className={`px-2 py-0.5 rounded text-xs border font-medium ${s.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s.status === "RETIRED" ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {s.status}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{s.sponsoring_authority} · {s.coverage_type?.replace(/_/g, " ")} · Grace {s.sanction_reversal_grace_days}d</p>
              </div>
              {s.status === "ACTIVE" && (
                <button
                  onClick={() => retire(s.id)}
                  className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                  data-testid={`retire-scheme-${s.scheme_code}`}
                >
                  Retire
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TAB 2: Records ────────────────────────────────────────────────────────────

function RecordsTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/api/finance/fm4c/records`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) { const d = await res.json(); setRecords(d.records || []); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="records-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{records.length} records</p>
        <button onClick={load} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400" data-testid="refresh-records-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No reimbursement records yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="records-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Scheme</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">Period</th>
                <th className="text-left py-2 px-3 text-xs text-slate-400 font-medium">4-Axis Status</th>
                <th className="text-right py-2 px-3 text-xs text-slate-400 font-medium">Govt Receivable</th>
                <th className="text-right py-2 px-3 text-xs text-slate-400 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`record-row-${r.id}`}>
                  <td className="py-2.5 px-3 text-slate-700">{r.scheme_name || "—"}</td>
                  <td className="py-2.5 px-3 text-slate-500 text-xs">{r.academic_period}</td>
                  <td className="py-2.5 px-3"><FourAxisRow record={r} /></td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs text-slate-700">{fmt(r.government_receivable)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs text-slate-700">{fmt(r.received_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── TAB 3: Eligibility Queue ──────────────────────────────────────────────────

function EligibilityQueueTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // { record_id, type: 'confirm'|'reject', amount, reason }
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/api/finance/fm4c/records?eligibility_status=ELIGIBILITY_PENDING&is_closed=false`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const d = await res.json();
      // Also get CONDITIONALLY_ELIGIBLE
      const res2 = await fetch(`${API}/api/finance/fm4c/records?eligibility_status=CONDITIONALLY_ELIGIBLE&is_closed=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d2 = res2.ok ? await res2.json() : { records: [] };
      setRecords([...(d.records || []), ...(d2.records || [])]);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!action) return;
    setSubmitting(true);
    const url = action.type === "confirm"
      ? `${API}/api/finance/fm4c/records/${action.record_id}/confirm-eligibility`
      : `${API}/api/finance/fm4c/records/${action.record_id}/reject-eligibility`;
    const body = action.type === "confirm"
      ? { eligible_amount: parseFloat(action.amount || 0) }
      : { reason: action.reason };
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      toast.success(action.type === "confirm" ? "Eligibility confirmed" : "Eligibility rejected");
      setAction(null);
      load();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Action failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="eligibility-queue-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{records.length} pending eligibility review{records.length !== 1 ? "s" : ""}</p>
        <button onClick={load} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {action && (
        <Section title={action.type === "confirm" ? "Confirm Eligibility" : "Reject Eligibility"}>
          <div className="space-y-3">
            {action.type === "confirm" ? (
              <Field label="Eligible Amount (₹) *">
                <input type="number" className={inputCls} min={0} step="0.01" value={action.amount || ""} onChange={e => setAction(a => ({ ...a, amount: e.target.value }))} data-testid="eligible-amount-input" />
              </Field>
            ) : (
              <Field label="Reason for Rejection *">
                <textarea className={inputCls} rows={2} value={action.reason || ""} onChange={e => setAction(a => ({ ...a, reason: e.target.value }))} data-testid="rejection-reason-input" />
              </Field>
            )}
            <div className="flex gap-2">
              <button onClick={doAction} disabled={submitting} className={`px-4 py-2 rounded-lg text-sm text-white transition-colors ${action.type === "confirm" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`} data-testid="confirm-eligibility-action-btn">
                {submitting ? "Saving..." : action.type === "confirm" ? "Confirm Eligibility" : "Reject Eligibility"}
              </button>
              <button onClick={() => setAction(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">Cancel</button>
            </div>
          </div>
        </Section>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <CheckCircle className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No eligibility reviews pending.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid={`eligibility-row-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.scheme_name} · {r.academic_period}</p>
                  <p className="text-xs text-slate-400 mt-0.5 font-mono">{r.student_account_id}</p>
                  <div className="mt-2"><AxisBadge value={r.eligibility_status} /></div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAction({ record_id: r.id, type: "confirm", amount: r.eligible_amount })}
                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs hover:bg-emerald-100 transition-colors"
                    data-testid={`confirm-elig-btn-${r.id}`}
                  >
                    <Check className="w-3 h-3" />Confirm
                  </button>
                  <button
                    onClick={() => setAction({ record_id: r.id, type: "reject", reason: "" })}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs hover:bg-red-100 transition-colors"
                    data-testid={`reject-elig-btn-${r.id}`}
                  >
                    <X className="w-3 h-3" />Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TAB 4: Claims Queue ───────────────────────────────────────────────────────

function ClaimsQueueTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r1 = await fetch(`${API}/api/finance/fm4c/records?is_closed=false`, { headers: { Authorization: `Bearer ${token}` } });
    if (r1.ok) {
      const d = await r1.json();
      setRecords((d.records || []).filter(r => ["READY_TO_CLAIM", "CLAIM_SUBMITTED", "ACKNOWLEDGED"].includes(r.claim_status)));
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const doSubmitClaim = async () => {
    if (!action) return;
    setSubmitting(true);
    const res = await fetch(`${API}/api/finance/fm4c/records/${action.record_id}/submit-claim`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ claim_reference: action.reference, claimed_amount: parseFloat(action.amount) }),
    });
    if (res.ok) { toast.success("Claim submitted"); setAction(null); load(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    setSubmitting(false);
  };

  const doAcknowledge = async (record_id) => {
    const ref = prompt("Enter authority acknowledgement reference:");
    if (!ref) return;
    const res = await fetch(`${API}/api/finance/fm4c/records/${record_id}/record-acknowledgement`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgement_reference: ref }),
    });
    if (res.ok) { toast.success("Claim acknowledged"); load(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="claims-queue-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{records.length} claim{records.length !== 1 ? "s" : ""} needing action</p>
        <button onClick={load} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {action && (
        <Section title="Submit Claim to Authority">
          <div className="space-y-3">
            <Field label="External Claim Reference *">
              <input className={inputCls} value={action.reference || ""} onChange={e => setAction(a => ({ ...a, reference: e.target.value }))} data-testid="claim-reference-input" />
            </Field>
            <Field label="Claimed Amount (₹) *">
              <input type="number" className={inputCls} min={0} step="0.01" value={action.amount || ""} onChange={e => setAction(a => ({ ...a, amount: e.target.value }))} data-testid="claimed-amount-input" />
            </Field>
            <div className="flex gap-2">
              <button onClick={doSubmitClaim} disabled={submitting} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors" data-testid="submit-claim-btn">
                {submitting ? "Submitting..." : "Submit Claim"}
              </button>
              <button onClick={() => setAction(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">Cancel</button>
            </div>
          </div>
        </Section>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No claims pending action.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid={`claim-row-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.scheme_name} · {r.academic_period}</p>
                  <p className="text-xs text-slate-400 font-mono">{r.student_account_id}</p>
                  <div className="flex gap-1.5 mt-2">
                    <AxisBadge value={r.claim_status} />
                    <span className="text-xs text-slate-400 self-center">{fmt(r.eligible_amount)} eligible</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {r.claim_status === "READY_TO_CLAIM" && (
                    <button onClick={() => setAction({ record_id: r.id, reference: "", amount: r.eligible_amount })}
                      className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs hover:bg-indigo-100"
                      data-testid={`file-claim-btn-${r.id}`}>
                      File Claim
                    </button>
                  )}
                  {r.claim_status === "CLAIM_SUBMITTED" && (
                    <button onClick={() => doAcknowledge(r.id)}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs hover:bg-blue-100"
                      data-testid={`acknowledge-btn-${r.id}`}>
                      Record Ack
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TAB 5: Sanctions ─────────────────────────────────────────────────────────

function SanctionsTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r1 = await fetch(`${API}/api/finance/fm4c/records?sanction_status=AWAITING_SANCTION&is_closed=false`, { headers: { Authorization: `Bearer ${token}` } });
    const r2 = await fetch(`${API}/api/finance/fm4c/records?sanction_status=REVERSAL_PENDING&is_closed=false`, { headers: { Authorization: `Bearer ${token}` } });
    const d1 = r1.ok ? await r1.json() : { records: [] };
    const d2 = r2.ok ? await r2.json() : { records: [] };
    setRecords([...(d1.records || []), ...(d2.records || [])]);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const doSanction = async () => {
    if (!action) return;
    setSubmitting(true);
    const url = action.type === "full"
      ? `${API}/api/finance/fm4c/records/${action.record_id}/record-sanction`
      : action.type === "partial"
      ? `${API}/api/finance/fm4c/records/${action.record_id}/record-partial-sanction`
      : `${API}/api/finance/fm4c/records/${action.record_id}/record-sanction-rejection`;
    const body = action.type === "reject"
      ? { rejected_amount: parseFloat(action.amount || 0), reason: action.reason, sanction_reference: action.reference }
      : { sanctioned_amount: parseFloat(action.amount || 0), sanction_reference: action.reference, charge_ids: action.charge_ids || [] };
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      toast.success(action.type === "reject" ? "Sanction rejection recorded" : "Sanction recorded");
      setAction(null); load();
    } else {
      const e = await res.json();
      toast.error(e.detail || "Failed");
    }
    setSubmitting(false);
  };

  const doConfirmReversal = async (record_id) => {
    if (!window.confirm("Confirm the sanction reversal? This is step 2 of dual-approval.")) return;
    const res = await fetch(`${API}/api/finance/fm4c/records/${record_id}/confirm-reversal`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (res.ok) { toast.success("Reversal confirmed"); load(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="sanctions-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{records.length} record{records.length !== 1 ? "s" : ""} awaiting sanction action</p>
        <button onClick={load} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {action && (
        <Section title={action.type === "full" ? "Record Sanction" : action.type === "partial" ? "Record Partial Sanction" : "Record Rejection"}>
          <div className="space-y-3">
            <Field label={action.type === "reject" ? "Rejected Amount (₹) *" : "Sanctioned Amount (₹) *"}>
              <input type="number" className={inputCls} min={0} step="0.01" value={action.amount || ""} onChange={e => setAction(a => ({ ...a, amount: e.target.value }))} data-testid="sanction-amount-input" />
            </Field>
            <Field label="Authority Reference">
              <input className={inputCls} value={action.reference || ""} onChange={e => setAction(a => ({ ...a, reference: e.target.value }))} data-testid="sanction-reference-input" />
            </Field>
            {action.type !== "reject" && (
              <Field label="Charge IDs (comma-separated, required for credit memo)">
                <input className={inputCls} placeholder="charge-uuid-1, charge-uuid-2"
                  value={(action.charge_ids || []).join(", ")}
                  onChange={e => setAction(a => ({ ...a, charge_ids: e.target.value.split(",").map(x => x.trim()).filter(Boolean) }))}
                  data-testid="charge-ids-input"
                />
              </Field>
            )}
            {action.type === "reject" && (
              <Field label="Reason *">
                <textarea className={inputCls} rows={2} value={action.reason || ""} onChange={e => setAction(a => ({ ...a, reason: e.target.value }))} data-testid="rejection-reason-input" />
              </Field>
            )}
            <div className="flex gap-2">
              <button onClick={doSanction} disabled={submitting}
                className={`px-4 py-2 rounded-lg text-sm text-white transition-colors ${action.type === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                data-testid="confirm-sanction-btn">
                {submitting ? "Saving..." : action.type === "reject" ? "Record Rejection" : "Record Sanction"}
              </button>
              <button onClick={() => setAction(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">Cancel</button>
            </div>
          </div>
        </Section>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Landmark className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No records awaiting sanction.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid={`sanction-row-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.scheme_name} · {r.academic_period}</p>
                  <p className="text-xs text-slate-400 font-mono">{r.student_account_id}</p>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    <AxisBadge value={r.sanction_status} />
                    <span className="text-xs text-slate-400 self-center">
                      Claimed: {fmt(r.claimed_amount)}
                    </span>
                  </div>
                </div>
                {r.sanction_status === "AWAITING_SANCTION" && (
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => setAction({ record_id: r.id, type: "full", amount: r.claimed_amount, reference: "", charge_ids: [] })}
                      className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-xs hover:bg-emerald-100"
                      data-testid={`record-sanction-btn-${r.id}`}>
                      Sanction
                    </button>
                    <button onClick={() => setAction({ record_id: r.id, type: "partial", amount: "", reference: "", charge_ids: [] })}
                      className="px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded text-xs hover:bg-teal-100"
                      data-testid={`partial-sanction-btn-${r.id}`}>
                      Partial
                    </button>
                    <button onClick={() => setAction({ record_id: r.id, type: "reject", amount: r.claimed_amount, reason: "", reference: "" })}
                      className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-xs hover:bg-red-100"
                      data-testid={`reject-sanction-btn-${r.id}`}>
                      Reject
                    </button>
                  </div>
                )}
                {r.sanction_status === "REVERSAL_PENDING" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-orange-600 bg-orange-50 border border-orange-200 px-2 py-1 rounded">
                      Awaiting dual-approval
                    </span>
                    <button onClick={() => doConfirmReversal(r.id)}
                      className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700"
                      data-testid={`confirm-reversal-btn-${r.id}`}>
                      Confirm Reversal
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TAB 6: Receipts ───────────────────────────────────────────────────────────

function ReceiptsTab({ token }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r1 = await fetch(`${API}/api/finance/fm4c/records?receipt_status=NONE_RECEIVED&is_closed=false`, { headers: { Authorization: `Bearer ${token}` } });
    const r2 = await fetch(`${API}/api/finance/fm4c/records?receipt_status=PARTIALLY_RECEIVED&is_closed=false`, { headers: { Authorization: `Bearer ${token}` } });
    const r3 = await fetch(`${API}/api/finance/fm4c/records?receipt_status=FULLY_RECEIVED&is_closed=false`, { headers: { Authorization: `Bearer ${token}` } });
    const d1 = r1.ok ? await r1.json() : { records: [] };
    const d2 = r2.ok ? await r2.json() : { records: [] };
    const d3 = r3.ok ? await r3.json() : { records: [] };
    const all = [
      ...(d1.records || []).filter(r => r.sanction_status === "SANCTIONED" || r.sanction_status === "PARTIALLY_SANCTIONED"),
      ...(d2.records || []),
      ...(d3.records || []),
    ];
    setRecords(all);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const doReceipt = async () => {
    if (!action) return;
    setSubmitting(true);
    const res = await fetch(`${API}/api/finance/fm4c/records/${action.record_id}/record-receipt`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ received_amount: parseFloat(action.amount), received_date: action.date, receipt_reference: action.reference, notes: action.notes }),
    });
    if (res.ok) { toast.success("Receipt recorded"); setAction(null); load(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    setSubmitting(false);
  };

  const doReconcile = async (record_id) => {
    const res = await fetch(`${API}/api/finance/fm4c/records/${record_id}/reconcile`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (res.ok) { toast.success("Record reconciled"); load(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  const doClose = async (record_id) => {
    if (!window.confirm("Close this record? It will become permanently read-only.")) return;
    const res = await fetch(`${API}/api/finance/fm4c/records/${record_id}/close`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (res.ok) { toast.success("Record closed"); load(); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="receipts-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{records.length} receipt{records.length !== 1 ? "s" : ""} pending</p>
        <button onClick={load} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {action && (
        <Section title="Record Institution Receipt">
          <div className="space-y-3">
            <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 px-3 py-2 rounded-lg">
              Institution-inbound receipt: funds received from external authority (reduces Government Receivable Outstanding — not Student Liability).
            </p>
            <Field label="Amount Received (₹) *">
              <input type="number" className={inputCls} min={0} step="0.01" value={action.amount || ""} onChange={e => setAction(a => ({ ...a, amount: e.target.value }))} data-testid="receipt-amount-input" />
            </Field>
            <Field label="Date Received *">
              <input type="date" className={inputCls} value={action.date || ""} onChange={e => setAction(a => ({ ...a, date: e.target.value }))} data-testid="receipt-date-input" />
            </Field>
            <Field label="Authority Payment Reference">
              <input className={inputCls} value={action.reference || ""} onChange={e => setAction(a => ({ ...a, reference: e.target.value }))} data-testid="receipt-reference-input" />
            </Field>
            <div className="flex gap-2">
              <button onClick={doReceipt} disabled={submitting} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors" data-testid="confirm-receipt-btn">
                {submitting ? "Saving..." : "Record Receipt"}
              </button>
              <button onClick={() => setAction(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">Cancel</button>
            </div>
          </div>
        </Section>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : records.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <ReceiptText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No receipts pending.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid={`receipt-row-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.scheme_name} · {r.academic_period}</p>
                  <p className="text-xs text-slate-400 font-mono">{r.student_account_id}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                    <AxisBadge value={r.receipt_status} />
                    <span className="text-xs text-slate-500">
                      Sanctioned {fmt(r.government_receivable)} · Received {fmt(r.received_amount)} · Outstanding {fmt(r.government_receivable_outstanding)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap justify-end">
                  {["NONE_RECEIVED", "PARTIALLY_RECEIVED"].includes(r.receipt_status) && (
                    <button onClick={() => setAction({ record_id: r.id, amount: r.government_receivable_outstanding, date: new Date().toISOString().split("T")[0], reference: "" })}
                      className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-xs hover:bg-emerald-100"
                      data-testid={`record-receipt-btn-${r.id}`}>
                      Record Receipt
                    </button>
                  )}
                  {r.receipt_status === "FULLY_RECEIVED" && (
                    <button onClick={() => doReconcile(r.id)}
                      className="px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded text-xs hover:bg-teal-100"
                      data-testid={`reconcile-btn-${r.id}`}>
                      Reconcile
                    </button>
                  )}
                  {r.receipt_status === "RECONCILED" && (
                    <button onClick={() => doClose(r.id)}
                      className="px-2.5 py-1 bg-slate-100 text-slate-600 border border-slate-200 rounded text-xs hover:bg-slate-200"
                      data-testid={`close-record-btn-${r.id}`}>
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

// ── FM4D: India Compliance Tab (BOUND-REOPEN-FM4D-02) ────────────────────────
// Adds supplementary India compliance workspace to the existing 6-tab staff view.
// Touches ONLY: IndiaComplianceTab component + TABS entry + CONTENT map entry.
// Existing 6 tabs, their state, and their FM4C action buttons are NOT modified.
function IndiaComplianceTab({ token }) {
  const { user } = useAuth();
  // india.configure → institution_admin, super_admin only (INV-FM4D-RBAC-01)
  const canConfigure = ["institution_admin", "super_admin"].includes(user?.role);
  const [configs, setConfigs] = useState([]);
  const [records, setRecords] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [configForm, setConfigForm] = useState({
    reimbursement_scheme_id: "", jurisdiction_type: "CENTRAL",
    jurisdiction_state_code: "", category_code_family: "", portal_type: "MANUAL",
    dbt_enabled: false, sanction_authority_label: "", claim_grace_days: 90,
    requires_income_certificate: true, requires_caste_certificate: false,
    requires_domicile_certificate: false,
  });
  const [submittingConfig, setSubmittingConfig] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [actionRecord, setActionRecord] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionForm, setActionForm] = useState({});
  const [submittingAction, setSubmittingAction] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const loadAll = useCallback(async () => {
    setLoadingConfigs(true);
    setLoadingRecords(true);
    try {
      const [cfgRes, recRes, schRes] = await Promise.all([
        fetch(`${API}/api/finance/fm4d/india/scheme-configs`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/finance/fm4d/india/compliance`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/finance/fm4c/schemes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (cfgRes.ok) { const d = await cfgRes.json(); setConfigs(d.scheme_configs || []); }
      if (recRes.ok) { const d = await recRes.json(); setRecords(d.compliance_records || []); }
      if (schRes.ok) { const d = await schRes.json(); setSchemes(d.schemes || []); }
    } catch (e) { toast.error("Failed to load India compliance data"); }
    setLoadingConfigs(false);
    setLoadingRecords(false);
  }, [API, token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submitConfig = async (e) => {
    e.preventDefault();
    setSubmittingConfig(true);
    try {
      const res = await fetch(`${API}/api/finance/fm4d/india/scheme-configs`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          ...configForm,
          claim_grace_days: parseInt(configForm.claim_grace_days) || 90,
          jurisdiction_state_code: configForm.jurisdiction_state_code || null,
          category_code_family: configForm.category_code_family || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create India config");
      toast.success("India scheme config created");
      setShowConfigForm(false);
      loadAll();
    } catch (err) { toast.error(err.message); }
    setSubmittingConfig(false);
  };

  const submitAction = async (e) => {
    e.preventDefault();
    if (!actionRecord || !actionType) return;
    setSubmittingAction(true);
    const endpoint = `${API}/api/finance/fm4d/india/compliance/${actionRecord}/${actionType}`;
    try {
      const res = await fetch(endpoint, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(actionForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Action failed");
      toast.success(data.message || "Updated");
      if (data.invariant_note) toast.info(data.invariant_note, { duration: 6000 });
      setActionRecord(null);
      setActionType(null);
      setActionForm({});
      loadAll();
    } catch (err) { toast.error(err.message); }
    setSubmittingAction(false);
  };

  const ACTION_LABELS = {
    "portal-submission": "Portal Submission",
    "authority-acknowledgement": "Authority Acknowledgement",
    "sanction-order": "Sanction Order (Evidence)",
    "treasury-reference": "Treasury Reference (Evidence)",
    "dbt-reference": "DBT Reference (Evidence)",
  };

  const ACTION_FIELDS = {
    "portal-submission": [
      { key: "portal_submission_reference", label: "Portal Reference No." },
      { key: "portal_submission_status", label: "Portal Status (operational text)" },
    ],
    "authority-acknowledgement": [
      { key: "authority_acknowledgement_ref", label: "Acknowledgement Reference *", required: true },
      { key: "authority_acknowledgement_date", label: "Acknowledgement Date (YYYY-MM-DD)" },
      { key: "district_officer_ref", label: "District Officer Reference" },
    ],
    "sanction-order": [
      { key: "sanction_order_reference", label: "Sanction Order Reference *", required: true },
      { key: "sanction_order_date", label: "Order Date (YYYY-MM-DD)" },
    ],
    "treasury-reference": [
      { key: "treasury_reference", label: "Treasury Challan Reference *", required: true },
      { key: "treasury_date", label: "Treasury Date (YYYY-MM-DD)" },
    ],
    "dbt-reference": [
      { key: "dbt_reference", label: "DBT / PFMS Reference *", required: true },
      { key: "dbt_date", label: "DBT Date (YYYY-MM-DD)" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="india-compliance-tab">
      {/* India Scheme Config Section */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">India Scheme Configurations</h3>
            <p className="text-xs text-slate-400 mt-0.5">Jurisdiction, portal type, eligibility criteria per scheme</p>
          </div>
          <button onClick={() => setShowConfigForm(!showConfigForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700"
            data-testid="btn-add-india-config">
            <Plus className="w-3.5 h-3.5" /> Add India Config
          </button>
        </div>
        {showConfigForm && (
          <form onSubmit={submitConfig} className="p-4 bg-slate-50 border-b border-slate-100 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Scheme *</label>
              <select className="w-full mt-1 border border-slate-200 rounded-lg text-sm p-2"
                value={configForm.reimbursement_scheme_id}
                onChange={e => setConfigForm(f => ({ ...f, reimbursement_scheme_id: e.target.value }))} required>
                <option value="">Select scheme…</option>
                {schemes.map(s => <option key={s.id} value={s.id}>{s.scheme_name} ({s.scheme_code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Jurisdiction Type *</label>
              <select className="w-full mt-1 border border-slate-200 rounded-lg text-sm p-2"
                value={configForm.jurisdiction_type}
                onChange={e => setConfigForm(f => ({ ...f, jurisdiction_type: e.target.value }))} required>
                <option value="CENTRAL">Central</option>
                <option value="STATE">State</option>
                <option value="STATE_CENTRAL_HYBRID">State-Central Hybrid</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">State Code (e.g. TG, AP)</label>
              <input className="w-full mt-1 border border-slate-200 rounded-lg text-sm p-2"
                placeholder="Leave blank for central schemes"
                value={configForm.jurisdiction_state_code}
                onChange={e => setConfigForm(f => ({ ...f, jurisdiction_state_code: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Category Family (e.g. SC_ST, OBC, EWS)</label>
              <input className="w-full mt-1 border border-slate-200 rounded-lg text-sm p-2"
                placeholder="Admin-configured text"
                value={configForm.category_code_family}
                onChange={e => setConfigForm(f => ({ ...f, category_code_family: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Portal Type *</label>
              <select className="w-full mt-1 border border-slate-200 rounded-lg text-sm p-2"
                value={configForm.portal_type}
                onChange={e => setConfigForm(f => ({ ...f, portal_type: e.target.value }))} required>
                <option value="MANUAL">Manual (offline)</option>
                <option value="NSP">NSP (National Scholarship Portal)</option>
                <option value="STATE_PORTAL">State Portal</option>
                <option value="DEPARTMENT_PORTAL">Department Portal</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Sanctioning Authority Label</label>
              <input className="w-full mt-1 border border-slate-200 rounded-lg text-sm p-2"
                placeholder="e.g. District Welfare Officer"
                value={configForm.sanction_authority_label}
                onChange={e => setConfigForm(f => ({ ...f, sanction_authority_label: e.target.value }))} />
            </div>
            <div className="col-span-2 flex gap-2">
              <button type="submit" disabled={submittingConfig}
                className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="btn-submit-india-config">
                {submittingConfig ? "Creating…" : "Create India Config"}
              </button>
              <button type="button" onClick={() => setShowConfigForm(false)}
                className="px-4 py-2 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300">
                Cancel
              </button>
            </div>
          </form>
        )}
        {loadingConfigs ? (
          <div className="p-4 text-center text-sm text-slate-400">Loading…</div>
        ) : configs.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">No India scheme configs yet. Add one above.</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {configs.map(c => (
              <div key={c.id} className="p-4 flex items-center justify-between" data-testid={`india-config-${c.id}`}>
                <div>
                  <p className="font-medium text-sm text-slate-800">{c.scheme_name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {c.jurisdiction_type}{c.jurisdiction_state_code ? ` · ${c.jurisdiction_state_code}` : ""} · {c.portal_type}
                    {c.category_code_family ? ` · ${c.category_code_family}` : ""}
                    {c.dbt_enabled ? " · DBT Enabled" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {c.sanction_authority_label && (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{c.sanction_authority_label}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* India Compliance Records Section */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800 text-sm">India Compliance Records</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Portal metadata, sanction evidence, treasury references — supplementary to FM4C 4-axis lifecycle
          </p>
        </div>
        {loadingRecords ? (
          <div className="p-4 text-center text-sm text-slate-400">Loading…</div>
        ) : records.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            <Globe className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No India compliance records yet.<br />
            <span className="text-xs">Create a compliance record via any FM4C record's detail view.</span>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {records.map(r => (
              <div key={r.id} className="p-4" data-testid={`india-compliance-row-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-slate-800">{r.scheme_name}</span>
                      <span className="text-xs text-slate-400">{r.academic_period}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: "Eligibility", val: r.eligibility_status },
                        { label: "Claim", val: r.claim_status },
                        { label: "Sanction", val: r.sanction_status },
                        { label: "Receipt", val: r.receipt_status },
                      ].map(s => (
                        <span key={s.label} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          {s.label}: {s.val}
                        </span>
                      ))}
                    </div>
                    {r.portal_submission_status && (
                      <p className="text-xs text-indigo-600 mt-1">
                        Portal: {r.portal_submission_status}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {r.authority_acknowledgement_ref && (
                        <span className="text-xs text-green-600">Ack: {r.authority_acknowledgement_ref}</span>
                      )}
                      {r.sanction_order_reference && (
                        <span className="text-xs text-amber-600 font-medium">
                          Sanction Order: {r.sanction_order_reference} (evidence only)
                        </span>
                      )}
                      {r.has_treasury_reference && (
                        <span className="text-xs text-blue-600">Treasury ref recorded (evidence only)</span>
                      )}
                      {r.has_dbt_reference && (
                        <span className="text-xs text-blue-600">DBT ref recorded (evidence only)</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => {
                        setExpandedRecord(expandedRecord === r.reimbursement_record_id ? null : r.reimbursement_record_id);
                      }}
                      className="text-xs px-2.5 py-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                      data-testid={`btn-expand-compliance-${r.id}`}>
                      {expandedRecord === r.reimbursement_record_id ? "Hide Actions" : "Actions"}
                    </button>
                  </div>
                </div>

                {expandedRecord === r.reimbursement_record_id && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400 mb-2">
                      All actions below record supplementary evidence metadata only.
                      They do not trigger FM4C axis transitions.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(ACTION_LABELS).map(([type, label]) => (
                        <button key={type}
                          onClick={() => { setActionRecord(r.reimbursement_record_id); setActionType(type); setActionForm({}); }}
                          className="text-xs px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                          data-testid={`btn-action-${type}`}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {actionRecord === r.reimbursement_record_id && actionType && (
                      <form onSubmit={submitAction} className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                        <p className="text-xs font-semibold text-amber-800">
                          {ACTION_LABELS[actionType]}
                          {["sanction-order", "treasury-reference", "dbt-reference"].includes(actionType) && (
                            <span className="ml-2 text-amber-600 font-normal">
                              (Evidence only — no FM4C lifecycle change)
                            </span>
                          )}
                        </p>
                        {(ACTION_FIELDS[actionType] || []).map(f => (
                          <div key={f.key}>
                            <label className="text-xs text-slate-600">{f.label}</label>
                            <input className="w-full mt-0.5 border border-slate-200 rounded text-sm p-1.5"
                              required={f.required}
                              value={actionForm[f.key] || ""}
                              onChange={e => setActionForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <button type="submit" disabled={submittingAction}
                            className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 disabled:opacity-50"
                            data-testid="btn-submit-india-action">
                            {submittingAction ? "Saving…" : "Save"}
                          </button>
                          <button type="button"
                            onClick={() => { setActionRecord(null); setActionType(null); setActionForm({}); }}
                            className="px-3 py-1.5 bg-slate-200 text-slate-700 text-xs rounded-lg hover:bg-slate-300">
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { id: "schemes", label: "Schemes", icon: Settings },
  { id: "records", label: "Records", icon: Users },
  { id: "eligibility", label: "Eligibility Queue", icon: CheckCircle },
  { id: "claims", label: "Claims Queue", icon: FileText },
  { id: "sanctions", label: "Sanctions", icon: Landmark },
  { id: "receipts", label: "Receipts", icon: ReceiptText },
  // FM4D BOUND-REOPEN-FM4D-02: India Compliance tab
  { id: "india", label: "India Compliance", icon: Globe },
];

export default function ReimbursementsAdmin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("schemes");
  const token = localStorage.getItem("academiaos_token") || localStorage.getItem("token");

  // FM4C-15: Students must not access the staff workspace directly.
  // Backend enforces 403 on all FM4C staff API calls anyway, but redirect early.
  const STUDENT_ROLES = ["student"];
  if (user && STUDENT_ROLES.includes(user.role)) {
    return <Navigate to="/my-finance" replace />;
  }

  const CONTENT = {
    schemes: <SchemesTab token={token} />,
    records: <RecordsTab token={token} />,
    eligibility: <EligibilityQueueTab token={token} />,
    claims: <ClaimsQueueTab token={token} />,
    sanctions: <SanctionsTab token={token} />,
    receipts: <ReceiptsTab token={token} />,
    // FM4D BOUND-REOPEN-FM4D-02: India Compliance tab
    india: <IndiaComplianceTab token={token} />,
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="reimbursements-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Landmark className="w-5 h-5 text-indigo-600" />
            <h1 className="text-xl font-bold text-slate-800">Reimbursements</h1>
          </div>
          <p className="text-sm text-slate-400">
            Government & employer fee reimbursements — external authority pays institution on student's behalf
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 bg-white border border-slate-200 rounded-xl p-1.5">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div data-testid={`tab-content-${activeTab}`}>
          {CONTENT[activeTab]}
        </div>
      </div>
    </div>
  );
}
